import html2canvas from "html2canvas";
import MarketRepository from "../../Main/repository/MarketRepository";
import MarketCartLocalstorage from "../../Main/interface/MarketCartLocalstorage";
import AppSettingController from "../../Main/controller/AppSettingController";
import MarketSettingLocalStorage from "../../Main/interface/MarketSettingLocalStorage";
import {get, sumBy, toNumber} from "lodash";
import InvoiceInterface from "../../Invoice/interface/invoiceInterface";
import Toast from "../../../utility/toast";
import React from "react";
import {handleFatalError} from "../../../utility/errorHandler";
import DiscountController from "../../Invoice/Controller/DiscountController";
import LogController from "../../../controller/LogController";
import i18n from "../../../i18n";

export default class InvoiceGeneratorController {
    generateInvoiceHTML(state: InvoiceInterface, desk?: string): HTMLElement {
        const card = MarketRepository.getCartMarket() as MarketCartLocalstorage;
        const setting = AppSettingController.getSetting() as MarketSettingLocalStorage;
        const extraCosts = setting.extraCosts;
        const lang = i18n.language === 'ar-IQ' ? 'ar-IQ' : 'fa-IR';
        const currentDate = new Date().toLocaleDateString(lang).replace(/[\u200e\u200f]/g, '');
        const currentTime = new Date().toLocaleTimeString(lang, {hour: '2-digit', minute: '2-digit'}).replace(/[\u200e\u200f]/g, '');
        const currentDay = new Date().toLocaleDateString(lang, {weekday: 'long'});
        const quantity = sumBy(state.card.items, item => toNumber(get(item, "quantity", 1)));
        const t = i18n.t.bind(i18n);
        const currency = setting.currency || t("currency.default");
        const hasTax = card.items?.some(item => toNumber(item.tax) > 0) ?? false;
        const totalCols = hasTax ? 6 : 5;
        const valueColspan = totalCols - 2;
        const subtotalColspan = totalCols - 3;

        const getItemsRows = () => {
            if (!card.items?.length) return '';
            return card.items.map((item, index) => {
                const itemQuantity = item.quantity || 1;
                if (itemQuantity === undefined || itemQuantity === null) {
                    handleFatalError(t("table.countingProductFailedError"));
                }
                const showPrice = !AppSettingController.isHiddenPrice();
                const itemTotal = item.final_price ?(~~itemQuantity) * item.final_price : (~~itemQuantity) * item.price;
                const itemTax = item.tax ? `${toNumber(item.tax).toLocaleString(lang)}%` : (0).toLocaleString(lang);
                return `
                <tr>
                    <td>${(index + 1).toLocaleString(lang)}</td>
                    <td>${item.title}</td>
                    <td>${toNumber(itemQuantity).toLocaleString(lang)}</td>
                    <td>${showPrice ? item.price.toLocaleString(lang) : '-'}</td>
                    ${hasTax ? `<td>${showPrice ? toNumber(itemTax).toLocaleString(lang) : '-'}</td>` : ''}
                    <td>${showPrice ? itemTotal.toLocaleString(lang) : '-'}</td>
                </tr>`;
            }).join('');
        };

        const getExtraCostsRows = () => {
            const showPrice = !AppSettingController.isHiddenPrice();
            if (!showPrice || !Array.isArray(extraCosts)) return '';
            return extraCosts
                .filter(t => t.state !== 'deactivate')
                .map(t => {
                    const price = t.type === 'percentage'
                        ? ~~(state.price.regular * (+t.extraCostsPercentage / 100))
                        : ~~(+t.extraCostsPrice);
                    return `
                <tr>
                    <td colspan="2">${t.extraCostsName}</td>
                    <td colspan="${valueColspan}">${price.toLocaleString(lang)} ${currency}</td>
                </tr>`;
                }).join('');
        };

        const showPrice = !AppSettingController.isHiddenPrice();
        const discountRow = showPrice
            ? `<tr><td colspan="2">${t("setting.discount")}</td><td colspan="${valueColspan}">${DiscountController.getDiscount(state).toLocaleString(lang)} ${currency}</td></tr>`
            : '';

        const totalPriceDiv = showPrice
            ? `
        <div class="total-price">
            <p>${t("table.payNum")} <span>${this.getPaymentModeValue(state)}</span> :</p>
            <p class="fa-num">${state.price.total.toLocaleString(lang)} ${currency}</p>
        </div>`
            : '';

        const footerText = setting.textUnderBill
            ? `<div class="footer-invoice"><p class="fa-num">${setting.textUnderBill}</p></div>`
            : '';

        const showQrCode = (!setting.showQrCode || !setting.qrCodeBase64) ? '':
            `<div class="qrcode-invoice">
                <img
                    src="data:image/png;base64,${setting.qrCodeBase64}"
                    alt="QR Code"
                />
            </div>`;

        const showDesk = !setting.showQrCode ? `<div class="desk-name fa-num">${desk ? desk : t("invoice.takeout")}</div>` : ''
        const showReceipt = !setting.showQrCode ? `<div class="invoice-number"><span>${t("invoice.receipt")}</span> <span class="number fa-num">${setting.receipt}</span></div>` : ''

        const qrDescription = setting.showQrCode
            ? `
        <div class="qr-description">
            <p>${t("qr.description")}</p>
        </div>`
            : '';

        const container = document.createElement('div');
        container.className = 'invoice-generator-container';
        container.innerHTML = `
    <div>
        <div class="invoice-header">
            <div class="invoice-header-names-container">
                <div class="data-names">
                    <div class="name-shop fa-num">${setting.name}</div>
                    <div class="data-date">
                        <div class="day fa-num">${currentDay}</div>
                        ${showDesk}
                    </div>
                </div>
            </div>
            <div class="invoice-header-date-container">
                <div class="date fa-num">${t("date")}: ${currentDate}</div>
                <div class="date fa-num">${t("hour")}: ${currentTime}</div>
                ${showReceipt}
            </div>
        </div>
        <div class="invoice-content">
            <table>
                <thead>
                    <tr>
                        <th>${t("table.row")}</th>
                        <th style="width: 80%;">${t("table.product")}</th>
                        <th>${t("table.count")}</th>
                        <th>${t("table.price")}</th>
                        ${hasTax ? `<th>${t("table.tax")}</th>` : ''}
                        <th>${t("table.additional")}</th>
                    </tr>
                </thead>
                <tbody>
                    ${getItemsRows()}
                    <tr>
                        <td colspan="2">${t("table.totalProducts")}</td>
                        <td>${quantity.toLocaleString(lang)}</td>
                        ${showPrice
                            ? `<td colspan="${subtotalColspan}">${state.price.regular.toLocaleString(lang)} ${currency} </td>`
                            : `<td colspan="${subtotalColspan}">-</td>`}
                    </tr>
                    ${getExtraCostsRows()}
                    ${discountRow}
                </tbody>
            </table>
            ${totalPriceDiv}
            ${qrDescription}
            ${showQrCode}
            ${footerText}
        </div>
        <div class="copy-right fa-num">
            <span>021-91035025</span>
            <span>netbee.online</span>
        </div>
    </div>`;

        return container;
    }

    getTestInvoicePrint(): HTMLElement {
        LogController.log("print-test", "print-test");

        const lang = i18n.language === 'ar-IQ' ? 'ar-IQ' : 'fa-IR';
        const currentDate = new Date().toLocaleDateString(lang).replace(/[\u200e\u200f]/g, '');
        const currentTime = new Date().toLocaleTimeString(lang, {hour: '2-digit', minute: '2-digit'}).replace(/[\u200e\u200f]/g, '');
        const currentDay = new Date().toLocaleDateString(lang, {weekday: 'long'});
        const setting = AppSettingController.getSetting() as MarketSettingLocalStorage;
        const t = i18n.t.bind(i18n);
        const currency = setting.currency || t("currency.default");
        const card = {
            items: [
                {title: t("invoice.testProduct1"), quantity: 2, price: 50000},
                {title: t("invoice.testProduct2"), quantity: 1, price: 75000},
            ],
        };

        const quantity = sumBy(card.items, item => toNumber(get(item, "quantity", 1)));

        const state = {
            price: {
                total: ~~sumBy(card.items, item => item.price * toNumber(get(item, "quantity", 1))),
                regular: ~~sumBy(card.items, item => item.price * toNumber(get(item, "quantity", 1))),
            }
        };

        const showPrice = !AppSettingController.isHiddenPrice();

        const getItemsRows = () => {
            if (!card.items?.length) return '';
            return card.items.map((item, index) => {
                const itemQuantity = item.quantity || 1;
                if (itemQuantity === undefined || itemQuantity === null) {
                    handleFatalError(t('table.countingProductFailedError'));
                }
                const itemTotal = (~~itemQuantity) * item.price;
                return `
                <tr>
                    <td>${(index + 1).toLocaleString(lang)}</td>
                    <td>${item.title}</td>
                    <td>${toNumber(itemQuantity).toLocaleString(lang)}</td>
                    <td>${showPrice ? item.price.toLocaleString(lang) : '-'}</td>
                    <td>${showPrice ? itemTotal.toLocaleString(lang) : '-'}</td>
                </tr>`;
            }).join('');
        };

        const discountRow = showPrice
            ? `<tr><td colspan="2">${t("setting.discount")}</td><td colspan="3">${(state.price.total - state.price.regular).toLocaleString(lang)} ${currency}</td></tr>`
            : '';

        const totalPriceDiv = showPrice
            ? `
        <div class="total-price">
            <p>${t("table.payNum")} <span>${this.getPaymentModeValue(state as InvoiceInterface)}</span> :</p>
            <p class="fa-num">${state.price.regular.toLocaleString(lang)} ${currency}</p>
        </div>`
            : '';

        const footerText = setting.textUnderBill
            ? `<div class="footer-invoice"><p class="fa-num">${setting.textUnderBill}</p></div>`
            : '';

        const showQrCode = (!setting.showQrCode || !setting.qrCodeBase64) ? '':
            `<div class="qrcode-invoice">
                <img
                    src="data:image/png;base64,${setting.qrCodeBase64}"
                    alt="QR Code"
                />
            </div>`;

        const showDesk = !setting.showQrCode ? `<div class="desk-name fa-num">${t("invoice.testDesk")}</div>` : ''
        const showReceipt = !setting.showQrCode ? `<div class="invoice-number"><span>${t("invoice.receipt")}</span> <span class="number fa-num">${setting.receipt}</span></div>` : ''

        const qrDescription = setting.showQrCode
            ? `
        <div class="qr-description">
            <p>${t("qr.description")}</p>
        </div>`
            : '';

        const container = document.createElement('div');
        container.className = 'invoice-generator-container';

        container.innerHTML = `
    <div>
        <div class="invoice-header">
            <div class="invoice-header-names-container">
                <div class="data-names">
                    <div class="name-shop fa-num">${t("invoice.testPrint")}</div>
                    <div class="data-date">
                        <div class="day fa-num">${currentDay}</div>
                        ${showDesk}                    
                    </div>
                </div>
            </div>
            <div class="invoice-header-date-container">
                <div class="date fa-num">${t("date")}: ${currentDate}</div>
                <div class="date fa-num">${t("hour")}: ${currentTime}</div>
                ${showReceipt}
            </div>
        </div>
        <div class="invoice-content">
            <table>
                <thead>
                    <tr>
                        <th>${t("table.row")}</th>
                        <th style="width: 80%;">${t("table.product")}</th>
                        <th>${t("table.count")}</th>
                        <th>${t("table.price")}</th>
                        <th>${t("table.additional")}</th>
                    </tr>
                </thead>
                <tbody>
                    ${getItemsRows()}
                    <tr>
                        <td colspan="2">${t("table.totalProducts")}</td>
                        <td>${quantity.toLocaleString(lang)}</td>
                        ${showPrice ? `<td colspan="2">${state.price.regular.toLocaleString(lang)} ${currency} </td>` : `<td colspan="2">-</td>`}
                    </tr>
                    ${discountRow}
                </tbody>
            </table>
            ${totalPriceDiv}
            ${qrDescription}
            ${showQrCode}
            ${footerText}
        </div>
        <div class="copy-right fa-num">
            <span>021-91035025</span>
            <span>netbee.online</span>
        </div>
    </div>`;

        return container;
    }

    async print(state: InvoiceInterface, desk?: string, ip?: string, port?: number) {
        LogController.log("print invoice", "print", {state, desk})
        const invoiceElement = this.generateInvoiceHTML(state, desk);
        document.body.appendChild(invoiceElement);

        const canvas = await html2canvas(invoiceElement);
        const base64image = canvas.toDataURL();

        const kiosk = get(window, "Kiosk", undefined) as any;
        if (kiosk) {
            if (!ip) kiosk.print(base64image.replace("data:image/png;base64,", ""));
            if (ip) kiosk.printNet(ip, port, base64image.replace("data:image/png;base64,", ""));
        }
        document.body.removeChild(invoiceElement);
    }

    downloadImage(state: InvoiceInterface, desk?: string) {
        const invoiceElement = this.generateInvoiceHTML(state, desk);
        document.body.appendChild(invoiceElement);
        html2canvas(invoiceElement).then((canvas) => {
            const base64image = canvas.toDataURL("image/png");
            const link = document.createElement('a');
            link.href = base64image;
            link.download = 'invoice.png';
            link.click();
            document.body.removeChild(invoiceElement);
        });
    }

    async printTest(ip?: string, port?: number, setLoading?: React.Dispatch<React.SetStateAction<boolean>>) {
        const invoiceElement = this.getTestInvoicePrint();
        if (setLoading) setLoading(true);
        document.body.appendChild(invoiceElement);
        await new Promise(resolve => setTimeout(resolve, 2000));
        const canvas = await html2canvas(invoiceElement);
        const base64image = canvas.toDataURL();
        const kiosk = get(window, "Kiosk", undefined) as any;
        try {
            if (!ip) kiosk.print(base64image.replace("data:image/png;base64,", ""));
            if (ip) kiosk.printNet(ip, port, base64image.replace("data:image/png;base64,", ""));
        } catch (e) {
            Toast.error(i18n.t("toast.printFailed"));
        } finally {
            if (setLoading) setLoading(false);
        }
        document.body.removeChild(invoiceElement);
    }

    private getPaymentModeValue(state: InvoiceInterface) {
        const {marketMode} = AppSettingController.getSetting() as MarketSettingLocalStorage;
        if (marketMode === 'pos-or-cash' && state.paymentMode === 'pos') return i18n.t("invoice.posPaymentMode");
        if (marketMode === 'pos-or-cash' && state.paymentMode === 'cash') return i18n.t("invoice.cashPaymentMode");
        if (marketMode === 'pos') return i18n.t("invoice.posPaymentMode");
        if (marketMode === 'cash') return i18n.t("invoice.cashPaymentMode");
        return '';
    }
}